import { type DatasourceDataResponse } from '@atlaskit/linking-types';
import { type CloudId } from '@databases/types/confluence';
import { getThrottledBatchCallback } from '@databases/utils/utils';
import { groupBy } from 'lodash-es';

import { type DatasourceType as ConnectionType } from '@canvas/atlassian-service/datasource';

import { transformConfluenceResult } from '../adapter/confluenceDatasource';

import { getDatasourceResponseByType } from './datasource';

const MAX_BATCH_SIZE = 100;

export const resolveConnectionContent = getThrottledBatchCallback<
  [
    ari: string,
    columns: string[],
    type: ConnectionType,
    cloudId: CloudId,
    connectionId: string
  ],
  {
    connectionId: string;
    datasourceResponse: DatasourceDataResponse;
  }[]
>(async (args) => {
  return [await resolveContentByDatasource(args)];
}, 300);

async function resolveContentByDatasource(
  args: Array<
    [
      ari: string,
      columns: string[],
      type: ConnectionType,
      cloudId: CloudId,
      connectionId: string
    ]
  >
): Promise<
  {
    connectionId: string;
    datasourceResponse: DatasourceDataResponse;
  }[]
> {
  const uniqueArgs = Array.from(new Map(args.map((arg) => [arg[0], arg])).values());
  // Creates a dictionary that groups requests for content for a datasource based on the type
  // of data (ie Confluence content data), the cloudId of the site where the content is stored,
  // and the connectionId for the connection being established or which is currently in use.
  const groupedArgs = groupBy(
    uniqueArgs,
    ([, , type, cloudId, connectionId]) => `${cloudId}::${type}::${connectionId}`
  );

  // Assigns values for the defined request groups, grouping all content ARIs in a database view
  // that correspond with a site cloudId as well as the requested data information corresponding to
  // the field types of the columns. If all content in the database is from a singular site, there
  // will only be ONE element in the groupedData. As such, the collection of ARIs for which information
  // will be request for will be unlimited at this point.
  const groupedData: {
    [key: string]: { aris: string[]; columns: string[] };
  } = {};
  Object.entries(groupedArgs).forEach(([key, group]) => {
    groupedData[key] = {
      aris: group.map(([ari]) => ari).flat(),
      columns: Array.from(new Set(group.map(([, columns]) => columns).flat())),
    };
  });

  return await Promise.all(
    Object.entries(groupedData).map(async ([groupKey, data]) => {
      const [cloudId, type, connectionId] = groupKey.split('::');
      const columns = data.columns;

      // Batches the ARIs of content of which data will be requested for into groups that can be
      // processed by the endpoint without an internal server error, preventing an overload in the
      // successive API call.
      const batchedData = batchContentRequest(data.aris);

      return await Promise.all(
        batchedData.map(async (ariBatch) => {
          let datasourceResponse = await getDatasourceResponseByType(
            type as ConnectionType,
            columns,
            {
              cloudId,
              contentARIs: ariBatch,
            }
          );

          if (type === 'confluence') {
            datasourceResponse = transformConfluenceResult(datasourceResponse);
          }

          return {
            connectionId,
            datasourceResponse,
          };
        })
      );
    })
  ).then((result) => result.flat());
}

function batchContentRequest(aris: string[]): string[][] {
  const batchedData = [];

  for (let i = 0; i < aris.length; i += MAX_BATCH_SIZE) {
    const batchAris = aris.slice(i, i + MAX_BATCH_SIZE);
    batchedData.push(batchAris);
  }

  return batchedData;
}
